import { setLegacyCheckboxes } from "data/app/appActions";
import { getLegacyCheckboxes } from "data/app/appSelectors";
import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Column, useFlexLayout, useResizeColumns, useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import RowContent from "./RowContent";

interface Props {
	tableColumns: Array<
		Column<{
			[key: string]: string;
		}> & { sticky?: string }
	>;
	tableData: {
		[key: string]: string;
	}[];
	legacyId: string;
	setRowsLength: (length: number) => void;
	disabled: boolean;
}

const Table = ({
	tableColumns,
	tableData,
	legacyId,
	setRowsLength,
	disabled,
}: Props) => {
	const SELECTED_ROW_COLOR = "#EEF7F8";
	const Z_INDEX_HEADER_OFFSET = 100;

	const legacyCheckboxes = useSelector(getLegacyCheckboxes);
	const checkboxes = useMemo(() => {
		return legacyCheckboxes[legacyId] || [];
	}, [legacyCheckboxes, legacyId]);
	const dispatch = useDispatch();

	// Setting up the table with react-table
	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
		useTable(
			{
				columns: tableColumns || [],
				data: tableData || [],
			},
			useFlexLayout,
			useResizeColumns,
			useSticky
		);

	const handleCheckboxClick = useCallback(
		(rowIndex: number, event: React.MouseEvent) => {
			if (disabled !== true) {
				event.preventDefault();
				const isOptions = [
					...document.querySelectorAll(".legacyArrayView-action"),
					...document.querySelectorAll("button.btn-link"),
				].some(elem => elem.contains(event.target as HTMLElement));

				if (!isOptions) {
					const newCheckboxes = [...checkboxes];
					checkboxes.includes(rowIndex)
						? newCheckboxes.splice(
								newCheckboxes.indexOf(rowIndex),
								1
						  )
						: newCheckboxes.push(rowIndex);
					dispatch(setLegacyCheckboxes(legacyId, newCheckboxes));
				}
			}
		},
		[checkboxes, disabled, dispatch, legacyId]
	);

	useEffect(() => {
		setRowsLength(rows.length);
	}, [rows, setRowsLength]);

	return (
		<div className="legacyArrayView-content">
			<div className="legacyArrayView-wrapper">
				<table
					{...getTableProps({ style: { minWidth: "100%" } })}
					className="legacyArrayView table sticky">
					<thead
						className="legacyArrayView-thead"
						style={{
							zIndex: rows.length + Z_INDEX_HEADER_OFFSET,
						}}>
						{headerGroups.map(headerGroup => (
							<tr {...headerGroup.getHeaderGroupProps()}>
								{headerGroup.headers.map((column, index) => {
									const isColumnTitle =
										index === 0 ||
										index ===
											headerGroup.headers.length - 1;
									return (
										<th
											{...column.getHeaderProps()}
											className={`${
												isColumnTitle
													? ""
													: "legacyArrayView-columnTitle"
											}`}>
											{column.render("Header")}
											{column.canResize && (
												<div
													{...column.getResizerProps()}
													className={`resizer ${
														column.isResizing
															? "isResizing"
															: ""
													}`}
												/>
											)}
										</th>
									);
								})}
							</tr>
						))}
					</thead>
					<tbody {...getTableBodyProps()}>
						{rows.map(row => {
							prepareRow(row);
							return (
								<tr
									{...row.getRowProps({
										style: {
											width: "100%",
											minWidth: "100%",
										},
									})}
									className={`legacyArrayView-tr`}
									onClick={handleCheckboxClick.bind(
										null,
										row.index
									)}
									key={row.id}>
									<RowContent
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										row={row as any}
										backgroundColor={
											checkboxes.includes(row.index)
												? SELECTED_ROW_COLOR
												: ""
										}
										rowsLength={rows.length}
									/>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default Table;
